import { GROUP_BY_COLUMN_DATA } from '../../shared/constants';
import { alignRightCellStyle, alignCenterCellStyle } from '@common/cellStyles';
import { legalEntityColumnDefinition, businessUnitColumnDefinition } from '../../../shared/columnDefinitions';
import { CellExportFormats } from '@common/xlsxExport/xlsxExport.constants';
import TaxExpenseTooltipRenderer from '../drillDetailLinkCellRenderer.component';
import { customNumeralValueFormatter } from '../../../shared/customNumeralValueFormatter';
import { NumeralTypes } from '@common/utils/formatNumberValue';
import { numeralValueFormatter } from '../../../shared/numeralValueFormatter';

export const TYPES = {
  FEDERAL: 'FEDERAL',
  STATE: 'STATE',
};

export default ({
  groupByName,
  groupByField,
  hasSourceCurrency,
  type,
  shouldDisplayZero,
  hideDecimals,
  negativeMinus,
}) => [
  {
    headerName: groupByName,
    field: groupByField,
    minWidth: 120,
    pinned: 'left',
    ...alignCenterCellStyle,
  },
  ...(groupByName === GROUP_BY_COLUMN_DATA.CC.groupByName
    ? [
      { ...legalEntityColumnDefinition, headerName: 'Legal Entity', field: 'LEGAL_ENTITY' },
      { ...businessUnitColumnDefinition, field: 'BUSINESS_UNIT' },
    ] : []
  ),
  {
    headerName: 'Currency',
    field: 'FUNC_CURRENCY',
    minWidth: 120,
    ...alignCenterCellStyle,
  },
  {
    headerName: 'Status',
    field: 'STATUS_NAME',
    minWidth: 120,
    ...alignCenterCellStyle,
  },
  {
    headerName: 'Income/(Loss) Before Tax',
    field: 'IBT',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Pre-Tax Adjustment',
    field: 'IBT_ADJ',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Adjusted Income/(Loss) Before Tax',
    field: 'ADJ_IBT',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  ...(type === TYPES.FEDERAL
    ? [
      {
        headerName: 'State Income Tax',
        field: 'STATE_CUR_TAX',
        minWidth: 100,
        valueFormatter: customNumeralValueFormatter({
          shouldDisplayZero,
          hideDecimals,
          negativeMinus,
          type: NumeralTypes.DECIMAL,
        }),
        exportFormat: CellExportFormats.NUMBER,
        ...alignRightCellStyle,
      },
      {
        headerName: 'State Adjustment',
        field: 'STATE_CUR_TAX_ADJ',
        minWidth: 100,
        cellRenderer: TaxExpenseTooltipRenderer,
        exportFormat: CellExportFormats.NUMBER,
        ...alignRightCellStyle,
      },
    ] : []
  ),
  {
    headerName: 'State Income Taxes After Credits',
    field: 'STATE_TAX_AFT_CR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
    hide: type === TYPES.FEDERAL,
  },
  {
    headerName: 'Total Tax Permanent Differences',
    field: 'PERMS',
    minWidth: 120,
    cellRenderer: TaxExpenseTooltipRenderer,
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Total Tax Temporary Differences',
    field: 'TEMPS',
    minWidth: 120,
    cellRenderer: TaxExpenseTooltipRenderer,
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Taxable Income/(Loss)',
    field: 'TXBL_INC',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  ...(type === TYPES.FEDERAL
    ? [
      {
        headerName: 'Utilize Prior Year NOLs',
        field: 'UTILIZE_PY_NOL',
        minWidth: 120,
        valueFormatter: customNumeralValueFormatter({
          shouldDisplayZero,
          hideDecimals,
          negativeMinus,
          type: NumeralTypes.DECIMAL,
        }),
        exportFormat: CellExportFormats.NUMBER,
        ...alignRightCellStyle,
      },
      {
        headerName: 'Taxable Income After NOL Utilization',
        field: 'TXBL_INC_AFT_NOL',
        minWidth: 120,
        valueFormatter: customNumeralValueFormatter({
          shouldDisplayZero,
          hideDecimals,
          negativeMinus,
          type: NumeralTypes.DECIMAL,
        }),
        exportFormat: CellExportFormats.NUMBER,
        ...alignRightCellStyle,
      },
      {
        headerName: 'Record Current Year NOL',
        field: 'RECORD_CY_NOL',
        minWidth: 120,
        valueFormatter: customNumeralValueFormatter({
          shouldDisplayZero,
          hideDecimals,
          negativeMinus,
          type: NumeralTypes.DECIMAL,
        }),
        exportFormat: CellExportFormats.NUMBER,
        ...alignRightCellStyle,
      },
    ] : []
  ),
  {
    headerName: 'Current Tax Rate',
    field: 'TAX_RATE',
    minWidth: 120,
    valueFormatter: numeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.PERCENTAGE,
    }),
    exportFormat: CellExportFormats.PERCENT_6,
    omitExportIfNull: true,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Current Tax Expense Before Credits',
    field: 'CUR_TAX_B4_CR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Total Credits and Other After Tax Adj',
    field: 'TOTAL_CR',
    minWidth: 120,
    cellRenderer: TaxExpenseTooltipRenderer,
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  ...(type === TYPES.FEDERAL
    ? [
      {
        headerName: 'Foreign Current Tax Expense',
        field: 'CUR_FTC',
        minWidth: 120,
        valueFormatter: customNumeralValueFormatter({
          shouldDisplayZero,
          hideDecimals,
          negativeMinus,
          type: NumeralTypes.DECIMAL,
        }),
        exportFormat: CellExportFormats.NUMBER,
        ...alignRightCellStyle,
      },
      {
        headerName: 'Foreign Deferred Tax Expense',
        field: 'DEF_FTC',
        minWidth: 120,
        valueFormatter: customNumeralValueFormatter({
          shouldDisplayZero,
          hideDecimals,
          negativeMinus,
          type: NumeralTypes.DECIMAL,
        }),
        exportFormat: CellExportFormats.NUMBER,
        ...alignRightCellStyle,
      },
      {
        headerName: 'Total Foreign Taxes',
        field: 'TOT_FTC',
        minWidth: 120,
        valueFormatter: customNumeralValueFormatter({
          shouldDisplayZero,
          hideDecimals,
          negativeMinus,
          type: NumeralTypes.DECIMAL,
        }),
        exportFormat: CellExportFormats.NUMBER,
        ...alignRightCellStyle,
      },
      {
        headerName: 'Credit Limitation',
        field: 'FTC_LIMIT',
        minWidth: 100,
        valueFormatter: customNumeralValueFormatter({
          shouldDisplayZero,
          hideDecimals,
          negativeMinus,
          type: NumeralTypes.DECIMAL,
        }),
        exportFormat: CellExportFormats.NUMBER,
        ...alignRightCellStyle,
      },
      {
        headerName: 'Total Benefitted Credits',
        field: 'BEN_FTC_CR',
        minWidth: 120,
        valueFormatter: customNumeralValueFormatter({
          shouldDisplayZero,
          hideDecimals,
          negativeMinus,
          type: NumeralTypes.DECIMAL,
        }),
        exportFormat: CellExportFormats.NUMBER,
        ...alignRightCellStyle,
      },
      {
        headerName: 'Current Year Temporary Adjustments',
        field: 'CY_TEMP',
        minWidth: 120,
        valueFormatter: customNumeralValueFormatter({
          shouldDisplayZero,
          hideDecimals,
          negativeMinus,
          type: NumeralTypes.DECIMAL,
        }),
        exportFormat: CellExportFormats.NUMBER,
        ...alignRightCellStyle,
      },
      {
        headerName: 'Net Operating NOL',
        field: 'NO_NOL',
        minWidth: 100,
        valueFormatter: customNumeralValueFormatter({
          shouldDisplayZero,
          hideDecimals,
          negativeMinus,
          type: NumeralTypes.DECIMAL,
        }),
        exportFormat: CellExportFormats.NUMBER,
        ...alignRightCellStyle,
      },
      {
        headerName: 'Federal Benefit of State',
        field: 'FBOS',
        minWidth: 100,
        valueFormatter: customNumeralValueFormatter({
          shouldDisplayZero,
          hideDecimals,
          negativeMinus,
          type: NumeralTypes.DECIMAL,
        }),
        exportFormat: CellExportFormats.NUMBER,
        ...alignRightCellStyle,
      },
      {
        headerName: 'Record Valuation Allowance',
        field: 'RECORD_VA',
        minWidth: 120,
        valueFormatter: customNumeralValueFormatter({
          shouldDisplayZero,
          hideDecimals,
          negativeMinus,
          type: NumeralTypes.DECIMAL,
        }),
        exportFormat: CellExportFormats.NUMBER,
        ...alignRightCellStyle,
      },
      {
        headerName: 'Total Deferreds',
        field: 'TOT_DEF',
        minWidth: 100,
        valueFormatter: customNumeralValueFormatter({
          shouldDisplayZero,
          hideDecimals,
          negativeMinus,
          type: NumeralTypes.DECIMAL,
        }),
        exportFormat: CellExportFormats.NUMBER,
        ...alignRightCellStyle,
      },
      {
        headerName: 'Deferred Tax Rate',
        field: 'DEF_TAX_RATE',
        minWidth: 100,
        valueFormatter: numeralValueFormatter({
          shouldDisplayZero,
          hideDecimals,
          negativeMinus,
          type: NumeralTypes.PERCENTAGE,
        }),
        exportFormat: CellExportFormats.PERCENT_6,
        ...alignRightCellStyle,
      },
      {
        headerName: 'Deferred Tax Expense',
        field: 'DEF_TAX_B4_FTC',
        minWidth: 100,
        valueFormatter: customNumeralValueFormatter({
          shouldDisplayZero,
          hideDecimals,
          negativeMinus,
          type: NumeralTypes.DECIMAL,
        }),
        exportFormat: CellExportFormats.NUMBER,
        ...alignRightCellStyle,
      },
    ] : []
  ),
  {
    headerName: 'Total After Tax Deferred Tax Expense',
    field: 'DEF_PR_CR_UTL',
    minWidth: 120,
    cellRenderer: TaxExpenseTooltipRenderer,
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Current Tax Expense on CY Activity',
    field: 'CUR_TAX',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Deferred Tax Expense on CY Activity',
    field: 'DEF_TAX',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Deferred Foreign Tax Credit',
    field: 'DEF_FTC',
    minWidth: 120,
    hide: type === TYPES.FEDERAL,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: !hasSourceCurrency
      ? 'Tax Expense on CY Activity'
      : 'Tax Expense on CY Activity (Functional Currency)',
    field: 'TAX_EXP_CY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'CY Effective Tax Rate',
    field: 'CY_EFF_TAX_RATE',
    minWidth: 120,
    valueFormatter: numeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.PERCENTAGE,
    }),
    exportFormat: CellExportFormats.PERCENT_6,
    omitExportIfNull: true,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Tax Currency',
    field: 'TAX_CURRENCY',
    minWidth: 120,
    hide: !hasSourceCurrency,
    ...alignCenterCellStyle,
  },
  {
    headerName: 'MOR',
    field: 'TAX_CUR_MOR',
    minWidth: 120,
    hide: !hasSourceCurrency,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Current Tax Expense on CY Activity',
    field: 'CUR_TAX_EXP_TAX_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    hide: !hasSourceCurrency,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Deferred Tax Expense on CY Activity',
    field: 'DEF_TAX_EXP_TAX_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    hide: !hasSourceCurrency,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Tax Expense - CY Activity (Tax Currency)',
    field: 'TAX_EXP_CY_TAX_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    hide: !hasSourceCurrency,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Reserve (Current)',
    field: 'RES_CUR_CY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Reserve (Non Current)',
    field: 'RES_NON_CUR_CY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Reserve (Interest)',
    field: 'RES_INT_CY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Reserve (Penalty)',
    field: 'RES_PEN_CY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Other Provision (Current)',
    field: 'OTH_PROV_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Total Other - CY Activity (Current) - Tax Currency',
    field: 'TOT_CUR_AFT_TAX_CY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Other Provision (Deferred)',
    field: 'OTH_PROV_DEF',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Total Other - CY Activity (Deferred) - Tax Currency',
    field: 'TOT_DEF_AFT_TAX_CY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Total Current Tax Expense on CY Activity - Tax Currency',
    field: 'TOT_CUR_TAX_EXP_CY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Total Deferred Tax Expense on CY Activity - Tax Currency',
    field: 'TOT_DEF_TAX_EXP_CY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Total Tax Expense on CY Activity - Tax Currency',
    field: 'TOT_TAX_EXP_CY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Return to Accrual (Current)',
    field: 'RTA_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Post Return Adjustments (Current)',
    field: 'PRA_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'True Up (Payable)',
    field: 'TRUE_UP_PAY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Reserve (Current)',
    field: 'RES_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Reserve (Non Current)',
    field: 'RES_NON_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Reserve (Interest)',
    field: 'RES_INT',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Reserve (Penalty)',
    field: 'RES_PEN',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Total Current Tax Expense - PY Activity - Tax Currency',
    field: 'TOT_CUR_AFT_TAX_PY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Return to Accrual (Deferred)',
    field: 'RTA_DEF',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Post Return Adjustments (Deferred)',
    field: 'PRA_DEF',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'True Up (Deferred)',
    field: 'TRUE_UP_DEF',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Rate Changes (Deferred)',
    field: 'RATE_CHG_DEF',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Total Deferred Tax Expense - PY Activity - Tax Currency',
    field: 'TOT_DEF_AFT_TAX_PY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Total Tax Expense on PY Activity - Tax Currency',
    field: 'TAX_EXP_PY_TAX_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  ...(type === TYPES.FEDERAL
    ? [
      {
        headerName: 'Current Tax Expense',
        field: 'CUR_TAX_EXP_PY_TAX_CUR',
        minWidth: 100,
        valueFormatter: customNumeralValueFormatter({
          shouldDisplayZero,
          hideDecimals,
          negativeMinus,
          type: NumeralTypes.DECIMAL,
        }),
        exportFormat: CellExportFormats.NUMBER,
        ...alignRightCellStyle,
      },
      {
        headerName: 'Deferred Tax Expense',
        field: 'DEF_TAX_EXP_PY_TAX_CUR',
        minWidth: 100,
        valueFormatter: customNumeralValueFormatter({
          shouldDisplayZero,
          hideDecimals,
          negativeMinus,
          type: NumeralTypes.DECIMAL,
        }),
        exportFormat: CellExportFormats.NUMBER,
        ...alignRightCellStyle,
      },
    ]
    : []
  ),
  {
    headerName: 'Total Tax Expense',
    field: 'TAX_EXP_TAX_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: !hasSourceCurrency
      ? 'Adjusted Income/(Loss) Before Tax'
      : 'Adjusted Income/(Loss) Before Tax (Tax Currency)',
    field: 'IBT_TAX_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Effective Tax Rate',
    field: 'ETR',
    minWidth: 120,
    valueFormatter: numeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.PERCENTAGE,
    }),
    exportFormat: CellExportFormats.PERCENT_6,
    omitExportIfNull: true,
    ...alignRightCellStyle,
  },
];
